import { isWithinInterval } from "date-fns";

import { DateTimeValue } from "../core";
import { isSameTime, parseTime } from "../lib/datetime";
import { AvailabilitySlot, BlockOutDates } from "./api";

export enum BookingType {
  Hourly = "hourly",
  Daily = "daily",
}

export interface DailyBooking {
  id: string;
  attendees: number;
  spaceID: string;
  spaceType: string;
  orderID: string;
  originalOrderId: string | null;
  orderConfirmationID: string;
  orderCreatedAt: Date | null;
  orderName: string;
  orderEmail: string;
  bookingType: BookingType.Daily;
  layoutID: string;
  room: string;
  floor: string;
  physicalSpaceID: string;
  days: number;
  startDate: string;
  endDate: string;
  cancellationAllowed: boolean;
  cancellationAllowedBefore: Date | null;
  time: string | null;
  duration: string;
  productType?: {
    id: string;
    name: string;
  };
}

export interface HourlyBooking {
  id: string;
  attendees: number;
  spaceID: string;
  spaceType: string;
  orderID: string;
  originalOrderId: string | null;
  orderConfirmationID: string;
  orderCreatedAt: Date | null;
  orderName: string;
  orderEmail: string;
  bookingType: BookingType.Hourly;
  room: string;
  floor: string;
  layoutID: string;
  physicalSpaceID: string;
  date: string;
  cancellationAllowed: boolean;
  cancellationAllowedBefore: Date | null;
  time: string;
  duration: string;
  productType?: {
    id: string;
    name: string;
  };
}

export type Booking = DailyBooking | HourlyBooking;

export interface BookingCoreValues {
  layoutID?: string;
  dateTime?: DateTimeValue;
  attendees?: number;
}

interface AvailableTimeSlot {
  type: "available";
}

interface UnavailableDayTimeSlot {
  type: "unavailable_day";
  description: string;
}

interface UnavailableTimeSlot {
  type: "unavailable";
  description: string;
}

interface BufferedTimeSlot {
  type: "buffered";
  description: string;
}

interface LeadTimeSlot {
  type: "lead_time";
  description: string;
}

interface BookedTimeSlot {
  type: "booked";
  description: string;
}

interface MinBookingHoursTimeSlot {
  type: "not_enough_min_booking_hours";
  description: string;
}

interface PastAvailableTimeSlot {
  type: "past_available";
  description: string;
}

interface BlockedOutTimeSlot {
  type: "blocked_out_dates";
  blockedOutDates: BlockOutDates;
}

export type TimeSlot =
  | AvailableTimeSlot
  | UnavailableDayTimeSlot
  | BufferedTimeSlot
  | LeadTimeSlot
  | MinBookingHoursTimeSlot
  | PastAvailableTimeSlot
  | BookedTimeSlot
  | UnavailableTimeSlot
  | BlockedOutTimeSlot;

export function getTimeSlot(
  hour: Date,
  open: boolean,
  availabilities: AvailabilitySlot[],
  blockedOutDates: BlockOutDates | null
): TimeSlot {
  if (open === false) {
    return {
      type: "unavailable_day",
      description: "Unavailable on this date",
    };
  }

  for (let i = 0; i < availabilities.length; i++) {
    const slot = availabilities[i];

    if (slot.type === "booked") {
      continue;
    }

    const start = parseTime(slot.from);
    const end = parseTime(slot.to);
    const withinInterval =
      isWithinInterval(hour, {
        start,
        end,
      }) && isSameTime(hour, end) === false;

    if (withinInterval) {
      switch (slot.type) {
        case "available":
          return {
            type: "available",
          };
        case "buffered":
          return {
            type: "buffered",
            description: "Unavailable due to booking buffer",
          };
        case "blocked_out":
          if (blockedOutDates === null) {
            // TODO:
            // After invalidating space cache, space_calendar is fetched first and then blocked out dates
            // Because there is a window in which they are not in sync, this will be blockOutDates will be null
            // Temporarily disable it like so
            return {
              type: "unavailable",
              description: "Not available at this hour",
            };
          }

          return {
            type: "blocked_out_dates",
            blockedOutDates,
          };
        case "not_enough_min_booking_hours":
          return {
            type: "not_enough_min_booking_hours",
            description: "Minimum booking hours not satisfied",
          };
        case "past_available":
          return {
            type: "past_available",
            description: "Unavailable because this is in the past",
          };
        case "lead_time":
          return {
            type: "lead_time",
            description: "Lead time",
          };
        default:
          return {
            type: "unavailable",
            description: "Not available at this hour",
          };
      }
    }
  }

  return {
    type: "unavailable",
    description: "Not available at this hour",
  };
}
